import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Container from "@mui/material/Container";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import CustomSwitch from "../../components/CustomSwitch.js";
import DataNotFound from "../../components/DataNotFound.js";
import ErrorDialog from "../../components/dialogs/ErrorDialog.js";
import LoadingBackdrop from "../../components/LoadingBackdrop.js";
import PageListHeader from "../../components/page-list/PageListHeader.js";
import PageListItem from "../../components/page-list/PageListItem.js";
import { useUser } from "../../components/UserContext.js";
import { getHosts, putHost } from "../../lib/apiConfig.js";
import { PAGE_LIST_CONTAINER_SX } from "../../lib/constants.js";
import { useFetch } from "../../lib/hooks/useFetch.js";

export default function VisibleHostsList({ locales }) {
  const { runId } = useUser();
  const [hosts, setHosts] = useState(null);
  const [hostToSave, setHostToSave] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isAscending, setIsAscending] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingHosts } = useFetch({
    endpoint: getHosts({ runId }),
    mustFetch: !hosts,
    onSuccess: setHosts,
    onFail: setError,
  });

  const { fetchData: saveHost, isFetching: isSavingHost } = useFetch({
    endpoint: putHost({ id: hostToSave?.id, runId }),
    onSuccess: () => {
      const updatedHosts = hosts.map((host) =>
        host.id === hostToSave.id ? { ...host, ...hostToSave } : host
      );

      setHosts(updatedHosts);
      setHostToSave(null);
    },
    onFail: (error) => {
      setError(error);
      setHostToSave(null);
    },
  });

  const filteredHosts = useMemo(() => {
    if (!hosts) return;

    const filtered = hosts.filter((host) =>
      host.serial.toLowerCase().includes(searchValue.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const serialA = a.serial.toLowerCase();
      const serialB = b.serial.toLowerCase();

      return isAscending
        ? serialA > serialB
          ? 1
          : -1
        : serialA < serialB
        ? 1
        : -1;
    });
  }, [isAscending, hosts, searchValue]);

  useEffect(() => {
    hostToSave?.id && saveHost(hostToSave);
  }, [hostToSave, saveHost]);

  const handleInput = ({ target }) => {
    setSearchValue(target.value);
  };

  const handleClearInput = () => {
    setSearchValue("");
  };

  const handleSort = () => {
    setIsAscending(!isAscending);
  };

  return isFetchingHosts || isSavingHost ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_LIST_CONTAINER_SX}>
      {Array.isArray(hosts) ? (
        <>
          <PageListHeader
            handleClearInput={handleClearInput}
            handleInput={handleInput}
            handleSort={handleSort}
            locales={locales}
            isAscending={isAscending}
            pageName="hosts"
            searchBoxLabel={locales["hosts.search"]}
            searchValue={searchValue}
          />
          {filteredHosts.length ? (
            <List>
              {filteredHosts.map((host, index) => {
                const { character, isHidden, serial } = host;

                return (
                  <PageListItem
                    Icon={
                      <CustomSwitch
                        checked={!isHidden}
                        handleChange={() =>
                          setHostToSave({ ...host, isHidden: !isHidden })
                        }
                        icon={VisibilityOffIcon}
                        checkedIcon={VisibilityIcon}
                      />
                    }
                    index={index}
                    key={index}
                    primaryText={serial}
                    secondaryText={character?.name}
                  />
                );
              })}
            </List>
          ) : (
            <Typography>{locales["search.empty"]}</Typography>
          )}
        </>
      ) : (
        <DataNotFound text={locales["hosts.notFound"]} />
      )}
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
